import type { DocumentKey } from './models'

/** State for uploading, downloading, and deleting knowledge-base attribute documents. */
export interface State {
  readonly deletions: EntryLookup<boolean>
  readonly downloads: EntryLookup<string>
  readonly selections: SelectionLookup
  readonly uploads: EntryLookup<void>
}

/** Data, loading, or failure produced by one document operation. */
export type Entry<TData> = ExactlyOneOf<{
  readonly data: TData
  readonly loading: true
  readonly error: Error
}>

/** Operation entries addressed by caller-supplied document key. */
export type EntryLookup<TData> = Partial<Record<DocumentKey, Entry<TData>>>

/** Files chosen for one document key, staged until upload is triggered. */
export type SelectionLookup = Partial<Record<DocumentKey, readonly File[]>>

export namespace State {
  export const INITIAL: Readonly<State> = {
    deletions: {},
    downloads: {},
    selections: {},
    uploads: {},
  }

  /** Sets the current operation entry for one document upload. */
  export function setUploadEntry(
    state: State,
    key: DocumentKey,
    entry: NonNullable<State['uploads'][DocumentKey]>,
  ): State {
    return { ...state, uploads: { ...state.uploads, [key]: entry } }
  }

  /** Clears operation state for the given document's upload. */
  export function removeUploadEntry(state: State, key: DocumentKey): State {
    const uploads = { ...state.uploads }
    delete uploads[key]
    return { ...state, uploads }
  }

  /** Sets the current operation entry for one document download. */
  export function setDownloadEntry(
    state: State,
    key: DocumentKey,
    entry: NonNullable<State['downloads'][DocumentKey]>,
  ): State {
    return { ...state, downloads: { ...state.downloads, [key]: entry } }
  }

  /** Clears operation state for the given document's download. */
  export function removeDownloadEntry(state: State, key: DocumentKey): State {
    const downloads = { ...state.downloads }
    delete downloads[key]
    return { ...state, downloads }
  }

  /** Sets the current operation entry for one document deletion. */
  export function setDeletionEntry(
    state: State,
    key: DocumentKey,
    entry: NonNullable<State['deletions'][DocumentKey]>,
  ): State {
    return { ...state, deletions: { ...state.deletions, [key]: entry } }
  }

  /** Clears operation state for the given document's deletion. */
  export function removeDeletionEntry(state: State, key: DocumentKey): State {
    const deletions = { ...state.deletions }
    delete deletions[key]
    return { ...state, deletions }
  }

  /** Stages the given files for one document key, replacing any prior selection. */
  export function setSelection(
    state: State,
    key: DocumentKey,
    files: readonly File[],
  ): State {
    return { ...state, selections: { ...state.selections, [key]: files } }
  }

  /** Clears the staged file selection for one document key. */
  export function removeSelection(state: State, key: DocumentKey): State {
    const selections = { ...state.selections }
    delete selections[key]
    return { ...state, selections }
  }
}
